// ============================================================================
// PROJECT TASKS TAB
// Displays project-scoped tasks in a filterable, sortable table with
// multi-select bulk actions (cancel, delete) and pagination.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  RotateCcw,
  Loader2,
  ClipboardList,
  Ban,
  AlertTriangle,
  Zap,
  Layers,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronLeft,
  Link,
  X,
  Trash2,
  Search,
  ArrowUpDown
} from 'lucide-react';
import { TaskCategory, TaskStatus } from '../../types/session-task.types';
import {
  projectTasksService,
  ProjectTaskSummary,
  ProjectTaskDetail
} from '../../services/sessionTasksService';
import { CreateProjectTaskModal } from './CreateProjectTaskModal';
import {
  ImageLightbox,
  renderOutputContent,
  getOutputTypeLabel,
  getOutputTypeBadge
} from '../session-tasks/SessionTaskDetailModal';

interface ProjectTasksTabProps {
  projectId: string;
  token: string;
}

const PAGE_SIZE = 25;

type SortField = 'name' | 'status' | 'category' | 'created' | 'duration';

const SortIcon: React.FC<{ isActive: boolean; direction: 'asc' | 'desc' }> = ({ isActive, direction }) => {
  if (!isActive) return <ArrowUpDown size={12} className="text-slate-300" />;
  return direction === 'asc'
    ? <ChevronUp size={12} className="text-primary" />
    : <ChevronDown size={12} className="text-primary" />;
};

export const ProjectTasksTab: React.FC<ProjectTasksTabProps> = ({
  projectId,
  token
}) => {
  const [tasks, setTasks] = useState<ProjectTaskSummary[]>([]);
  const [taskStats, setTaskStats] = useState({ active: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<TaskCategory | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<{ taskId: string } | null>(null);

  // Table state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [bulkLoading, setBulkLoading] = useState(false);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const loadStats = useCallback(async () => {
    try {
      const allTasks = await projectTasksService.listTasks(projectId, {});
      setTaskStats({
        active: allTasks.filter(t => t.status === 'running' || t.status === 'pending' || t.status === 'not_started').length,
        completed: allTasks.filter(t => t.status === 'completed').length,
        failed: allTasks.filter(t => t.status === 'failed').length,
      });
    } catch {
      // stats are best-effort
    }
  }, [projectId]);

  const loadTasks = useCallback(async () => {
    setLoading(prev => tasks.length > 0 ? false : prev);
    setError(null);

    try {
      const filters: any = {};
      if (filterCategory !== 'all') {
        filters.category = [filterCategory];
      }
      if (filterStatus !== 'all') {
        filters.status = [filterStatus];
      }

      const loadedTasks = await projectTasksService.listTasks(projectId, filters);
      setTasks(loadedTasks);
    } catch (err: any) {
      setError('Failed to load tasks: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, filterCategory, filterStatus, tasks.length]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Poll for updates when there are running tasks
  useEffect(() => {
    const hasRunning = tasks.some(t => t.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(() => { loadTasks(); loadStats(); }, 15000);
    return () => clearInterval(interval);
  }, [tasks, loadTasks, loadStats]);

  // Reset page and selection when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [filterCategory, filterStatus, searchQuery]);

  const handleTaskCreated = () => {
    setShowCreateModal(false);
    loadTasks();
    loadStats();
  };

  const handleRerunTask = async (taskId: string) => {
    try {
      await projectTasksService.rerunTask(projectId, taskId);
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Failed to rerun task: ' + err.message);
    }
  };

  const handleCancelTask = async (taskId: string) => {
    try {
      await projectTasksService.cancelTask(projectId, taskId);
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Failed to cancel task: ' + err.message);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Delete this task permanently?')) return;
    try {
      await projectTasksService.deleteTask(projectId, taskId);
      setSelectedIds(prev => { const next = new Set(prev); next.delete(taskId); return next; });
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Failed to delete task: ' + err.message);
    }
  };

  const handleBulkCancel = async () => {
    const runningIds = Array.from(selectedIds).filter(id =>
      tasks.find(t => t.taskId === id)?.status === 'running'
    );
    if (runningIds.length === 0) return;
    if (!window.confirm(`Cancel ${runningIds.length} running task${runningIds.length !== 1 ? 's' : ''}?`)) return;

    setBulkLoading(true);
    try {
      await projectTasksService.bulkCancel(projectId, runningIds);
      setSelectedIds(new Set());
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Bulk cancel failed: ' + err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const deletableIds = Array.from(selectedIds).filter(id => {
      const task = tasks.find(t => t.taskId === id);
      return task && task.status !== 'running' && task.scope === 'project';
    });
    if (deletableIds.length === 0) return;
    if (!window.confirm(`Permanently delete ${deletableIds.length} task${deletableIds.length !== 1 ? 's' : ''}?`)) return;

    setBulkLoading(true);
    try {
      await projectTasksService.bulkDelete(projectId, deletableIds);
      setSelectedIds(new Set());
      loadTasks();
      loadStats();
    } catch (err: any) {
      alert('Bulk delete failed: ' + err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const isTaskStale = (task: ProjectTaskSummary): boolean => {
    if (task.status !== 'running' || !task.startedAt) return false;
    const elapsed = Date.now() - new Date(task.startedAt).getTime();
    return elapsed > 10 * 60 * 1000;
  };

  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const durationMs = end - start;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const getDurationMs = (task: ProjectTaskSummary): number => {
    if (!task.startedAt) return 0;
    const start = new Date(task.startedAt).getTime();
    const end = task.completedAt ? new Date(task.completedAt).getTime() : Date.now();
    return end - start;
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  // Search + sort + paginate
  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.taskName.toLowerCase().includes(q));
    }
    return result;
  }, [tasks, searchQuery]);

  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks];
    const dir = sortDirection === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      switch (sortField) {
        case 'name':
          return dir * a.taskName.localeCompare(b.taskName);
        case 'status':
          return dir * a.status.localeCompare(b.status);
        case 'category':
          return dir * a.taskCategory.localeCompare(b.taskCategory);
        case 'created':
          return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        case 'duration':
          return dir * (getDurationMs(a) - getDurationMs(b));
        default:
          return 0;
      }
    });

    return sorted;
  }, [filteredTasks, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedTasks.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedTasks = sortedTasks.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = sortedTasks.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, sortedTasks.length);

  // Header checkbox indeterminate state
  const pageIds = paginatedTasks.map(t => t.taskId);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  const somePageSelected = pageIds.some(id => selectedIds.has(id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const toggleSelect = (taskId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(taskId) ? next.delete(taskId) : next.add(taskId);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'created' ? 'desc' : 'asc');
    }
  };

  const statusDot = (status: string) => {
    switch (status) {
      case 'running':
        return (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        );
      case 'completed':
        return <span className="inline-flex rounded-full h-2 w-2 bg-green-500" />;
      case 'failed':
        return <span className="inline-flex rounded-full h-2 w-2 bg-red-500" />;
      case 'cancelled':
        return <span className="inline-flex rounded-full h-2 w-2 bg-amber-400" />;
      default:
        return <span className="inline-flex rounded-full h-2 w-2 bg-slate-300" />;
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'running': return 'text-primary';
      case 'completed': return 'text-green-600';
      case 'failed': return 'text-red-500';
      case 'cancelled': return 'text-amber-600';
      default: return 'text-slate-500';
    }
  };

  // Bulk action counts
  const selectedRunningCount = Array.from(selectedIds).filter(id =>
    tasks.find(t => t.taskId === id)?.status === 'running'
  ).length;
  const selectedDeletableCount = Array.from(selectedIds).filter(id => {
    const task = tasks.find(t => t.taskId === id);
    return task && task.status !== 'running' && task.scope === 'project';
  }).length;

  return (
    <div className="flex flex-col gap-6">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-2 rounded-xl p-5 bg-white border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-0 top-0 h-full w-1 bg-blue-500 rounded-r-xl" />
          <div className="flex justify-between items-start">
            <div>
              <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Active Tasks</p>
              <p className="text-[#052831] text-3xl font-bold mt-1">{taskStats.active}</p>
            </div>
            <div className="size-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
              <Zap size={20} />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-5 bg-white border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-0 top-0 h-full w-1 bg-green-500 rounded-r-xl" />
          <div className="flex justify-between items-start">
            <div>
              <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Completed</p>
              <p className="text-[#052831] text-3xl font-bold mt-1">{taskStats.completed}</p>
            </div>
            <div className="size-10 rounded-full bg-green-50 flex items-center justify-center text-green-500">
              <CheckCircle size={20} />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-5 bg-white border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-0 top-0 h-full w-1 bg-red-500 rounded-r-xl" />
          <div className="flex justify-between items-start">
            <div>
              <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Failed</p>
              <p className="text-[#052831] text-3xl font-bold mt-1">{taskStats.failed}</p>
            </div>
            <div className="size-10 rounded-full bg-red-50 flex items-center justify-center text-red-500">
              <XCircle size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters + Search + Create */}
      <div className="bg-white rounded-xl border border-slate-200 p-2 shadow-sm flex flex-col md:flex-row justify-between items-center gap-3">
        <div className="flex w-full md:w-auto items-center gap-3">
          <div className="flex gap-1 p-1 bg-slate-50 rounded-lg">
            {(['all', 'thinking', 'doing'] as const).map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filterCategory === cat
                    ? 'bg-white shadow-sm font-bold text-[#052831]'
                    : 'hover:bg-white/50 text-slate-500'
                }`}
              >
                {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Status:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as TaskStatus | 'all')}
              className="text-sm font-medium text-[#052831] border-none bg-transparent cursor-pointer focus:outline-none"
            >
              <option value="all">Any</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-56">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search tasks..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
            />
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm shadow-primary/20 hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            <Plus size={16} />
            Create Task
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
          <span className="text-sm font-bold text-[#052831]">
            {selectedIds.size} selected
          </span>
          <div className="h-4 w-px bg-slate-300" />
          {selectedRunningCount > 0 && (
            <button
              onClick={handleBulkCancel}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50"
            >
              <Ban size={12} />
              Cancel {selectedRunningCount} running
            </button>
          )}
          {selectedDeletableCount > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              <Trash2 size={12} />
              Delete {selectedDeletableCount}
            </button>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl border border-red-200 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && tasks.length === 0 && (
        <div className="rounded-xl bg-white p-12 shadow-sm border border-slate-200 text-center">
          <Loader2 size={32} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-500 text-sm font-medium">Loading tasks...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && tasks.length === 0 && (
        <div className="rounded-xl bg-white p-12 shadow-sm border border-slate-200 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <ClipboardList size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 text-sm">No project tasks yet. Create your first task to get started!</p>
        </div>
      )}

      {/* Tasks Table */}
      {!loading && tasks.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="w-10 px-3 py-3">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300 text-primary focus:ring-primary/30"
                    />
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button onClick={() => handleSort('status')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                      Status <SortIcon isActive={sortField === 'status'} direction={sortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button onClick={() => handleSort('name')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                      Name <SortIcon isActive={sortField === 'name'} direction={sortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button onClick={() => handleSort('category')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                      Category <SortIcon isActive={sortField === 'category'} direction={sortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500">
                    Source
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button onClick={() => handleSort('created')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                      Created <SortIcon isActive={sortField === 'created'} direction={sortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button onClick={() => handleSort('duration')} className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700">
                      Duration <SortIcon isActive={sortField === 'duration'} direction={sortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-right text-xs uppercase tracking-wider font-semibold text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedTasks.map(task => (
                  <tr
                    key={task.taskId}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${
                      selectedIds.has(task.taskId) ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => setSelectedTask({ taskId: task.taskId })}
                  >
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(task.taskId)}
                        onChange={() => toggleSelect(task.taskId)}
                        className="rounded border-slate-300 text-primary focus:ring-primary/30"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <span className={`flex items-center gap-1.5 text-sm font-medium ${statusLabel(task.status)}`}>
                        {statusDot(task.status)}
                        <span className="capitalize">{task.status}</span>
                        {isTaskStale(task) && (
                          <AlertTriangle size={12} className="text-red-500 ml-0.5" />
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm font-medium text-[#052831] hover:text-primary transition-colors truncate block max-w-[300px]">
                        {task.taskName}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        {task.templateId.includes('graph-sync') && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-semibold rounded-md border border-slate-200">System</span>
                        )}
                        {task.taskCategory === 'thinking'
                          ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-md">Thinking</span>
                          : <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-md">Doing</span>
                        }
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {task.scope === 'session' && task.sourceSessionName ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md text-xs font-medium truncate max-w-[150px]">
                          <Layers size={10} /> {task.sourceSessionName}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Project</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm text-slate-500">{formatTimeAgo(task.createdAt)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm text-slate-500">
                        {task.startedAt ? formatDuration(task.startedAt, task.completedAt) : '—'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {task.status === 'completed' && (
                          <button
                            onClick={() => setSelectedTask({ taskId: task.taskId })}
                            className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                            title="View results"
                          >
                            <Eye size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => handleRerunTask(task.taskId)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                          title="Rerun"
                        >
                          <RotateCcw size={14} />
                        </button>
                        {task.status === 'running' && (
                          <button
                            onClick={() => handleCancelTask(task.taskId)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                            title="Cancel"
                          >
                            <Ban size={14} />
                          </button>
                        )}
                        {task.status !== 'running' && task.scope === 'project' && (
                          <button
                            onClick={() => handleDeleteTask(task.taskId)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <span className="text-xs text-slate-500">
              Showing {rangeStart}–{rangeEnd} of {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <span className="px-2 text-xs text-slate-500">
                Page {safePage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No search results */}
      {!loading && tasks.length > 0 && sortedTasks.length === 0 && searchQuery.trim() && (
        <div className="rounded-xl bg-white p-8 shadow-sm border border-slate-200 text-center">
          <Search size={24} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No tasks match "{searchQuery}"</p>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <CreateProjectTaskModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          projectId={projectId}
          onSuccess={handleTaskCreated}
        />
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <ProjectTaskDetailAdapter
          projectId={projectId}
          taskId={selectedTask.taskId}
          onClose={() => setSelectedTask(null)}
          onRerun={(taskId) => { setSelectedTask(null); handleRerunTask(taskId); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// PROJECT TASK DETAIL ADAPTER
// Fetches project task details and renders with rich output rendering
// (markdown, mermaid, images, Stitch designs, BCAF assessments)
// ============================================================================

const ProjectTaskDetailAdapter: React.FC<{
  projectId: string;
  taskId: string;
  onClose: () => void;
  onRerun: (taskId: string) => void;
}> = ({ projectId, taskId, onClose, onRerun }) => {
  const [task, setTask] = useState<ProjectTaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'outputs' | 'steps'>('outputs');
  const [expandedOutputs, setExpandedOutputs] = useState<Set<string>>(
    new Set(['synthesis', 'diagram_code', 'diagram_image', 'bcaf_assessment'])
  );
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  const loadTask = useCallback(async () => {
    try {
      const detail = await projectTasksService.getTask(projectId, taskId);
      setTask(detail);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [projectId, taskId]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  // Poll while running
  useEffect(() => {
    if (!task || task.status !== 'running') return;
    const interval = setInterval(loadTask, 5000);
    return () => clearInterval(interval);
  }, [task, loadTask]);

  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const ms = end - start;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const toggleOutput = (type: string) => {
    setExpandedOutputs(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  const toggleStep = (stepId: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      next.has(stepId) ? next.delete(stepId) : next.add(stepId);
      return next;
    });
  };

  return (
    <>
      {/* Image lightbox */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {/* Backdrop */}
      <div onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl w-[94%] max-w-[1200px] max-h-[92vh] overflow-hidden z-[1001] shadow-2xl flex flex-col">
        {/* Header */}
        <header className="border-b border-slate-100 p-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-4">
            <div className="space-y-3">
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight m-0">
                {task?.taskName || 'Loading...'}
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                {task && (
                  <>
                    {task.templateId.includes('graph-sync') && (
                      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                        System
                      </div>
                    )}
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
                      task.taskCategory === 'thinking' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {task.taskCategory.charAt(0).toUpperCase() + task.taskCategory.slice(1)}
                    </div>
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
                      task.status === 'running' ? 'bg-primary/10 text-primary' :
                      task.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                      task.status === 'failed' ? 'bg-red-50 text-red-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {task.status === 'running' && <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />}
                      {task.status === 'completed' && <CheckCircle size={14} />}
                      {task.status === 'failed' && <XCircle size={14} />}
                      {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                    </div>
                    {task.startedAt && (
                      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">
                        <Clock size={14} />
                        {formatDuration(task.startedAt, task.completedAt)}
                      </div>
                    )}
                    {task.currentStepName && task.status === 'running' && (
                      <div className="text-slate-400 font-medium text-sm ml-1">
                        Current step: <span className="text-slate-600">{task.currentStepName}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {task && (task.status === 'completed' || task.status === 'failed') && (
                <button
                  onClick={() => onRerun(taskId)}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <RotateCcw size={14} />
                  Rerun
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Tab Bar */}
        {task && (
          <nav className="flex px-6 border-b border-slate-100">
            <button
              onClick={() => setActiveTab('outputs')}
              className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors ${
                activeTab === 'outputs'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              Results ({task.outputs?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('steps')}
              className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors ${
                activeTab === 'steps'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              Steps ({task.stepResults?.length || 0})
            </button>
          </nav>
        )}

        {/* Body */}
        <main className="flex-1 overflow-auto bg-slate-50 p-6 space-y-4">
          {loading && !task && (
            <div className="text-center py-10 text-slate-500">
              <Loader2 size={24} className="animate-spin mx-auto mb-3" />
              Loading task details...
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {task && activeTab === 'outputs' && (
            <div className="space-y-4">
              {(!task.outputs || task.outputs.length === 0) ? (
                <div className="text-center py-10 text-slate-500 bg-white rounded-xl border border-slate-200">
                  {task.status === 'running'
                    ? 'Task is still running... Results will appear here when complete.'
                    : task.status === 'failed'
                    ? 'Task failed before producing results.'
                    : 'No outputs available.'}
                </div>
              ) : (
                task.outputs.map((output) => (
                  <section key={output.type} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div
                      onClick={() => toggleOutput(output.type)}
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                      style={expandedOutputs.has(output.type) ? { borderBottom: '1px solid #f1f5f9' } : {}}
                    >
                      <div className="flex items-center gap-3">
                        {expandedOutputs.has(output.type)
                          ? <ChevronDown size={16} className="text-slate-400" />
                          : <ChevronRight size={16} className="text-slate-400" />}
                        <h3 className="font-bold text-slate-900 m-0">
                          {getOutputTypeLabel(output.type)}
                        </h3>
                        <span className="px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          {getOutputTypeBadge(output.type)}
                        </span>
                        {output.source && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Link size={10} />
                            {output.source}
                          </span>
                        )}
                      </div>
                    </div>

                    {expandedOutputs.has(output.type) && (
                      <div className="p-6">
                        {renderOutputContent(output.content, output.type, setLightboxImage)}
                      </div>
                    )}
                  </section>
                ))
              )}
            </div>
          )}

          {task && activeTab === 'steps' && (
            <div className="space-y-2">
              {(!task.stepResults || task.stepResults.length === 0) ? (
                <div className="text-center py-10 text-slate-500 bg-white rounded-xl border border-slate-200">
                  No steps recorded yet.
                </div>
              ) : (
                task.stepResults.map((step, idx) => (
                  <section key={step.stepId} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div
                      onClick={() => (step.output || step.error) ? toggleStep(step.stepId) : undefined}
                      className={`flex items-center justify-between p-4 ${
                        step.output || step.error ? 'cursor-pointer hover:bg-slate-50/50' : ''
                      } transition-colors`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                          step.status === 'running' ? 'bg-blue-50 text-blue-600' :
                          step.status === 'failed' ? 'bg-red-50 text-red-600' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{step.stepName}</div>
                          <div className="text-[11px] text-slate-400 font-mono">{step.stepId}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${
                          step.status === 'completed' ? 'text-emerald-500' :
                          step.status === 'running' ? 'text-blue-500' :
                          step.status === 'failed' ? 'text-red-500' :
                          'text-slate-400'
                        }`}>
                          {step.status}
                        </span>
                        {step.startedAt && step.completedAt && (
                          <span className="text-[11px] text-slate-400">
                            {formatDuration(step.startedAt, step.completedAt)}
                          </span>
                        )}
                        {(step.output || step.error) && (
                          expandedSteps.has(step.stepId)
                            ? <ChevronDown size={14} className="text-slate-400" />
                            : <ChevronRight size={14} className="text-slate-400" />
                        )}
                      </div>
                    </div>

                    {expandedSteps.has(step.stepId) && (
                      <div className="p-4 bg-slate-50 border-t border-slate-100">
                        {step.error && (
                          <div className={`bg-red-50 border border-red-200 rounded-lg p-3 text-[13px] text-red-800 ${step.output ? 'mb-3' : ''}`}>
                            {step.error}
                          </div>
                        )}
                        {step.output && renderOutputContent(step.output)}
                      </div>
                    )}
                  </section>
                ))
              )}
            </div>
          )}
        </main>
      </div>
    </>
  );
};
